import { z } from 'zod';

const customEntrySchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(60),
  amount: z.coerce.number().min(0).default(0),
});

/**
 * Deliberately a different shape from `customEntrySchema` (`monthlyEMI`,
 * not `amount`) — Office_project's `EmployeeSalary` model reused the same
 * `{name, amount}` schema for `loanDeductions`, but `payrollEngine.js`
 * reads `l.monthlyEMI` off those entries, a field that schema never had.
 * Every loan deduction there computed as `NaN` (silently corrupting
 * `totalDeductions`/`netPay` for anyone with a loan configured) — fixed
 * here by giving loan entries the field the engine actually expects.
 */
const loanDeductionSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(60),
  monthlyEMI: z.coerce.number().min(0).default(0),
});

/**
 * One `EmployeeSalary` record per person — their CTC, which
 * `SalaryStructure` template it breaks down under, and personal
 * adjustments/identifiers layered on top. Ported from Office_project's
 * `EmployeeSalary` model. `assignEmployeeSalary` (the service) upserts by
 * `userId` — one endpoint handles both the initial assignment and later
 * updates, same as the source.
 */
export const assignEmployeeSalarySchema = z.object({
  userId: z.string().min(1, 'userId is required'),
  salaryStructureId: z.string().min(1, 'salaryStructureId is required'),
  ctc: z.coerce.number().positive('Annual CTC must be greater than 0'),

  customAllowances: z.array(customEntrySchema).default([]),
  customDeductions: z.array(customEntrySchema).default([]),
  loanDeductions: z.array(loanDeductionSchema).default([]),

  bankAccountNumber: z.string().trim().max(40).optional().or(z.literal('')),
  bankName: z.string().trim().max(120).optional().or(z.literal('')),
  ifscCode: z.string().trim().max(20).optional().or(z.literal('')),
  panNumber: z.string().trim().max(20).optional().or(z.literal('')),
  pfAccountNumber: z.string().trim().max(40).optional().or(z.literal('')),
  esiNumber: z.string().trim().max(40).optional().or(z.literal('')),

  effectiveFrom: z.coerce.date().optional(),
});

export type AssignEmployeeSalaryInput = z.infer<typeof assignEmployeeSalarySchema>;
